import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setStructuredData } from "@/lib/structuredData";
import { localizePath, stripLocale } from "@/lib/localePath";

const SUPPORTED_LANGS = ["en", "es", "pt", "de", "zh"];

const SITE_BASE =
  (process.env.REACT_APP_SITE_BASE_URL || "https://withaurora.love").replace(
    /\/$/,
    ""
  );

const ROUTE_SEO = {
  "/": { titleKey: "seo.home.title", descKey: "seo.home.description" },
  "/faith": { titleKey: "seo.faith.title", descKey: "seo.faith.description" },
  "/features": { titleKey: "seo.features.title", descKey: "seo.features.description" },
  "/faq": { titleKey: "seo.faq.title", descKey: "seo.faq.description" },
  "/best-ai-couples-apps-2026": {
    titleKey: "seo.comparison.title",
    descKey: "seo.comparison.description",
  },
  "/privacy-vs-llm": {
    titleKey: "seo.privacyLlm.title",
    descKey: "seo.privacyLlm.description",
  },
  "/knowledge": {
    titleKey: "seo.knowledge.title",
    descKey: "seo.knowledge.description",
  },
  "/christian": { titleKey: "seo.christian.title", descKey: "seo.christian.description" },
  "/for-men": { titleKey: "seo.men.title", descKey: "seo.men.description" },
  "/after-a-fight": { titleKey: "seo.afterAFight.title", descKey: "seo.afterAFight.description" },
  "/reconnect": { titleKey: "seo.reconnect.title", descKey: "seo.reconnect.description" },
  "/new-parents": { titleKey: "seo.newParents.title", descKey: "seo.newParents.description" },
  "/long-distance": { titleKey: "seo.longDistance.title", descKey: "seo.longDistance.description" },
  "/counseling-vs-divorce": {
    titleKey: "seo.counseling.title",
    descKey: "seo.counseling.description",
  },
  "/privacy": { titleKey: "seo.privacy.title", descKey: "seo.privacy.description" },
  "/roadmap": { titleKey: "seo.roadmap.title", descKey: "seo.roadmap.description" },
  "/story": { titleKey: "seo.story.title", descKey: "seo.story.description" },
  "/support": { titleKey: "seo.support.title", descKey: "seo.support.description" },
  "/changelog": { titleKey: "seo.changelog.title", descKey: "seo.changelog.description" },
  "/stores": { titleKey: "seo.home.title", descKey: "seo.home.description" },
};

const DEFAULT_SEO = {
  title: "withAurora.love | Relationship Operating System",
  description:
    "Build deeper connection through small daily moments. Private by design. Faith-aware optional.",
};

const stripHtmlAliases = (path) => path.replace(/\.html$/, "");
const normalizePath = (path) => {
  const normalized = stripHtmlAliases(path);
  return normalized === "" ? "/" : normalized;
};

const getSupportedLang = (language) => {
  const lang = (language || "en").slice(0, 2);
  return SUPPORTED_LANGS.includes(lang) ? lang : "en";
};

const isGuidePath = (path, slug) => path.startsWith("/knowledge/") && Boolean(slug);

const getGuideSeo = ({ i18n, lang, slug, t }) => {
  const guideTitle = i18n.getResource(lang, "guides", `${slug}.title`);
  const guideIntro = i18n.getResource(lang, "guides", `${slug}.intro`);
  return {
    title: `${guideTitle || slug} — withAurora.love`,
    description: guideIntro || t("site:seo.knowledge.description", ""),
  };
};

const getRouteSeo = ({ path, t }) => {
  const cfg = ROUTE_SEO[path] || ROUTE_SEO["/"];
  return {
    title: t(`site:${cfg.titleKey}`, DEFAULT_SEO.title),
    description: t(`site:${cfg.descKey}`, DEFAULT_SEO.description),
  };
};

const getSeoPayload = ({ path, slug, i18n, lang, t }) =>
  isGuidePath(path, slug)
    ? getGuideSeo({ i18n, lang, slug, t })
    : getRouteSeo({ path, t });

const getOgKicker = ({ path, slug, isGuide, t }) => {
  if (isGuide && slug) return t("site:knowledge.kicker", "Knowledge");
  const cfg = ROUTE_SEO[path] || ROUTE_SEO["/"];
  const kickerKey =
    cfg.titleKey?.replace(".title", ".kicker") || "seo.home.kicker";
  return t(`site:${kickerKey}`, "");
};

const getOgTitle = ({ isGuide, i18n, lang, slug, title }) => {
  if (!isGuide || !slug) return title;
  return i18n.getResource(lang, "guides", `${slug}.title`) || title;
};

const buildCanonicalHref = (path, lang) => `${SITE_BASE}${localizePath(path, lang)}`;

const upsertLink = (rel, hreflang, href) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

const upsertMeta = (name, content, attr = "name") => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const resetAlternateLinks = () => {
  document.head
    .querySelectorAll('link[rel="alternate"][data-aurora-hreflang]')
    .forEach((el) => el.remove());
};

const appendAlternateLink = (hreflang, href) => {
  const link = document.createElement("link");
  link.setAttribute("rel", "alternate");
  link.setAttribute("hreflang", hreflang);
  link.setAttribute("href", href);
  link.setAttribute("data-aurora-hreflang", "1");
  document.head.appendChild(link);
};

const applyAlternateLinks = (path) => {
  resetAlternateLinks();
  SUPPORTED_LANGS.forEach((lng) => {
    appendAlternateLink(lng, `${SITE_BASE}${localizePath(path, lng)}`);
  });
  appendAlternateLink("x-default", `${SITE_BASE}${localizePath(path, "en")}`);
};

const applyBasicSeo = ({ title, description }) => {
  if (title) document.title = title;
  if (!description) return;
  upsertMeta("description", description);
  upsertMeta("og:title", title, "property");
  upsertMeta("og:description", description, "property");
  upsertMeta("twitter:title", title);
  upsertMeta("twitter:description", description);
};

const applyOgImageSeo = ({ title, path, slug, i18n, lang, t }) => {
  const guide = isGuidePath(path, slug);
  const ogKicker = getOgKicker({ path, slug, isGuide: guide, t });
  const ogTitle = getOgTitle({ isGuide: guide, i18n, lang, slug, title });
  // Homepage gets the branded, art-directed share card; inner pages keep
  // the dynamic per-title OG endpoint.
  const isHome = path === "/";
  const ogImageUrl = isHome
    ? `${SITE_BASE}/assets/media/og-card.jpg`
    : `${SITE_BASE}/api/og?title=${encodeURIComponent(ogTitle)}` +
      `&kicker=${encodeURIComponent(ogKicker || "withAurora.love")}` +
      `&lang=${lang}`;

  upsertMeta("og:image", ogImageUrl, "property");
  upsertMeta("og:image:width", "1200", "property");
  upsertMeta("og:image:height", "630", "property");
  upsertMeta("og:image:type", isHome ? "image/jpeg" : "image/png", "property");
  upsertMeta("twitter:image", ogImageUrl);
  upsertMeta("twitter:card", "summary_large_image");
};

const ORG_ID = `${SITE_BASE}/#organization`;
const WEBSITE_ID = `${SITE_BASE}/#website`;

const buildOrganization = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "withAurora.love",
  url: `${SITE_BASE}/`,
  logo: `${SITE_BASE}/assets/withaurora-wordmark-dark.png`,
});

const buildWebsite = (lang) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "withAurora.love",
  url: `${SITE_BASE}/`,
  inLanguage: lang,
  publisher: { "@id": ORG_ID },
});

const buildSoftwareApplication = (description) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "withAurora.love",
  applicationCategory: "LifestyleApplication",
  operatingSystem: "iOS, Android, Web",
  url: `${SITE_BASE}/`,
  description,
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  publisher: { "@id": ORG_ID },
});

const buildGuideStructuredData = ({ title, description, canonicalHref, lang, guideTitle }) => [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    inLanguage: lang,
    mainEntityOfPage: canonicalHref,
    author: { "@type": "Organization", name: "withAurora.love", url: `${SITE_BASE}/` },
    publisher: { "@id": ORG_ID },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_BASE}${localizePath("/", lang)}` },
      { "@type": "ListItem", position: 2, name: "Knowledge", item: `${SITE_BASE}${localizePath("/knowledge", lang)}` },
      { "@type": "ListItem", position: 3, name: guideTitle || title, item: canonicalHref },
    ],
  },
];

const applyStructuredData = ({ path, slug, seo, canonicalHref, lang, i18n }) => {
  const graph = [buildOrganization(), buildWebsite(lang)];
  if (isGuidePath(path, slug)) {
    const guideTitle = i18n.getResource(lang, "guides", `${slug}.title`);
    graph.push(...buildGuideStructuredData({ ...seo, canonicalHref, lang, guideTitle }));
  } else if (path === "/") {
    graph.push(buildSoftwareApplication(seo.description));
  }
  setStructuredData("route", graph);
};

export const SeoTags = () => {
  const { pathname } = useLocation();
  const { slug } = useParams();
  const { t, i18n } = useTranslation(["site", "guides"]);

  useEffect(() => {
    const lang = getSupportedLang(i18n.language);
    const { path: localeless } = stripLocale(pathname);
    const path = normalizePath(localeless);
    const seo = getSeoPayload({ path, slug, i18n, lang, t });

    document.documentElement.setAttribute("lang", lang);
    applyBasicSeo(seo);
    applyOgImageSeo({ ...seo, path, slug, i18n, lang, t });

    const canonicalHref = buildCanonicalHref(path, lang);
    upsertLink("canonical", null, canonicalHref);
    upsertMeta("og:url", canonicalHref, "property");

    applyAlternateLinks(path);
    applyStructuredData({ path, slug, seo, canonicalHref, lang, i18n });
  }, [pathname, slug, i18n, i18n.language, t]);

  return null;
};
